import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  BanknotesIcon, ClockIcon, ExclamationTriangleIcon,
  CalendarDaysIcon, CheckCircleIcon, GiftIcon,
} from '@heroicons/react/24/outline';

const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const hdrs = Object.keys(rows[0]);
  const esc  = v => { const s=v==null?'':String(v); return s.includes(',')||s.includes('"')?'"'+s.replace(/"/g,'""')+'"':s; };
  const csv  = [hdrs.join(','), ...rows.map(r=>hdrs.map(h=>esc(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], {type:'text/csv;charset=utf-8;'});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href=url; a.download=filename+'.csv'; a.click();
  URL.revokeObjectURL(url);
};
const exportXLSX = async (rows, filename) => {
  try { const X=await import('xlsx'); const ws=X.utils.json_to_sheet(rows); const wb=X.utils.book_new(); X.utils.book_append_sheet(wb,ws,'Sheet1'); X.writeFile(wb,filename+'.xlsx'); }
  catch { exportCSV(rows, filename); }
};

const fmt        = n => Number(n||0).toLocaleString('en-PK',{minimumFractionDigits:0,maximumFractionDigits:0});
const fmtDate    = d => new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short'});
const dayOfWeek  = d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(d).getDay()];
const curMonth   = () => new Date().toISOString().slice(0,7);
const monthLabel = m => new Date(m+'-01').toLocaleDateString('en-PK',{month:'long',year:'numeric'});
const MONTHS     = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tsal * { box-sizing:border-box; }
  .tsal { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tsalFade .4s ease both; }
  @keyframes tsalFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tsal-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; margin-bottom:3px; }
  .tsal-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.5rem; }

  /* Tabs */
  .tsal-tabs { display:flex; gap:4px; background:#f0f2f7; border-radius:10px; padding:3px; margin-bottom:1.2rem; width:fit-content; }
  .tsal-tab  { padding:7px 18px; border-radius:8px; border:none; background:transparent; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .tsal-tab.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.08); }

  /* Toolbar */
  .tsal-toolbar { display:flex; align-items:center; gap:10px; margin-bottom:1.1rem; flex-wrap:wrap; }
  .tsal-month   { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; color:#0f1123; }
  .tsal-year    { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; color:#0f1123; width:100px; }
  .export-btns  { display:flex; gap:5px; margin-left:auto; }
  .btn-exp      { display:inline-flex; align-items:center; gap:5px; padding:7px 13px; border-radius:9px; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; border:1.5px solid; transition:all .12s; }
  .btn-exp.csv  { background:white; color:#6b7194; border-color:#e0e4f0; }
  .btn-exp.csv:hover  { background:#f0f2f7; color:#3b6ef8; border-color:#3b6ef8; }
  .btn-exp.xlsx { background:white; color:#10c98e; border-color:#a7f3d0; }
  .btn-exp.xlsx:hover { background:#ecfdf5; border-color:#10c98e; }

  /* Stat cards */
  .stat-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:1rem; margin-bottom:1.2rem; }
  .stat-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.1rem; display:flex; align-items:center; gap:10px; min-width:0; }
  .stat-card > div:last-child { min-width:0; flex:1; }
  .s-icon    { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .s-icon svg{ width:18px; height:18px; stroke-width:2; }
  .s-icon.green  { background:#ecfdf5; color:#10c98e; }
  .s-icon.red    { background:#fef2f2; color:#ef4444; }
  .s-icon.amber  { background:#fffbeb; color:#f59e0b; }
  .s-icon.blue   { background:#eef2ff; color:#3b6ef8; }
  .s-icon.purple { background:#f5f3ff; color:#8b5cf6; }
  .s-label { font-size:.68rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:2px; }
  .s-value { font-size:1.05rem; font-weight:700; color:#0f1123; overflow-wrap:break-word; }
  .s-sub   { font-size:.66rem; color:#9094a8; margin-top:1px; overflow-wrap:break-word; }

  /* Panel */
  .tsal-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.2rem; }
  .tsal-panel-hd { padding:1rem 1.3rem; border-bottom:1px solid #f0f2f7; font-size:.82rem; font-weight:700; color:#0f1123; }

  /* Day table */
  .tsal-tbl-wrap { overflow-x:auto; }
  .tsal-tbl { width:100%; border-collapse:collapse; }
  .tsal-tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tsal-tbl th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tsal-tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .1s; }
  .tsal-tbl tbody tr:last-child { border-bottom:none; }
  .tsal-tbl tbody tr:hover { background:#fafbff; }
  .tsal-tbl td { padding:10px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .tsal-tbl tr.total-row td { background:#fafbff; font-weight:700; border-top:2px solid #edf0f9; }
  .mono  { font-family:'JetBrains Mono',monospace; font-size:.76rem; }

  /* Status pills */
  .status-pill { display:inline-flex; align-items:center; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .status-pill.present  { background:#ecfdf5; color:#10c98e; }
  .status-pill.absent   { background:#fef2f2; color:#ef4444; }
  .status-pill.on-leave { background:#eef2ff; color:#3b6ef8; }
  .status-pill.holiday  { background:#f3f0ff; color:#8b5cf6; }
  .status-pill.off      { background:#f0f2f7; color:#6b7194; }

  /* Breakdown */
  .breakdown { background:#fafbff; border-radius:12px; border:1px solid #edf0f9; padding:1.2rem; margin:1rem 1.3rem 1.3rem; }
  .breakdown-title { font-weight:700; font-size:.88rem; color:#0f1123; margin-bottom:.8rem; }
  .brow     { display:flex; justify-content:space-between; font-size:.82rem; margin-bottom:5px; }
  .brow-net { display:flex; justify-content:space-between; font-size:.9rem; font-weight:700; color:#0f1123; padding-top:8px; margin-top:4px; border-top:2px solid #edf0f9; }

  /* Yearly grid */
  .yr-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; }
  .yr-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; cursor:pointer; transition:box-shadow .15s; }
  .yr-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.06); border-color:#a7f3d0; }
  .yr-card-month { font-size:.88rem; font-weight:700; color:#0f1123; margin-bottom:.6rem; }
  .yr-card-net   { font-size:1rem; font-weight:700; font-family:'JetBrains Mono',monospace; margin-bottom:.4rem; }
  .yr-card-info  { font-size:.7rem; color:#9094a8; margin-bottom:.5rem; }

  /* History */
  .hist-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:.8rem; }
  .hist-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:.9rem 1rem; cursor:pointer; transition:all .15s; }
  .hist-card:hover { border-color:#3b6ef8; box-shadow:0 2px 8px rgba(59,110,248,.08); }
  .hist-month  { font-size:.82rem; font-weight:700; color:#0f1123; margin-bottom:4px; }
  .hist-net    { font-size:.96rem; font-weight:700; color:#10c98e; font-family:'JetBrains Mono',monospace; }
  .hist-status { margin-top:5px; }

  /* States */
  .tsal-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .tsal-spin    { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  .tsal-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .no-data    { padding:2rem; text-align:center; color:#9094a8; font-size:.82rem; }

  @media (max-width:640px) { .stat-grid { grid-template-columns:1fr 1fr; } .yr-grid { grid-template-columns:1fr 1fr; } .hist-grid { grid-template-columns:1fr 1fr; } }

  /* ── Mobile cards ── */
  .tsal-cards { display:none; padding:10px; }
  .tsal-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .tsal-card.total { background:white; border:2px solid #e8ebf4; margin-bottom:0; }
  .tc-top   { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .tc-date  { font-family:'JetBrains Mono',monospace; font-size:.8rem; font-weight:600; color:#0f1123; }
  .tc-day   { font-size:.68rem; color:#9094a8; margin-top:1px; }
  .tc-row   { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .tc-row:last-child { border-bottom:none; }
  .tc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  @media (max-width:768px) { .tsal-tbl-wrap { display:none !important; } .tsal-cards { display:block; } }
`;

const TeacherSalary = () => {
  const [view,    setView]    = useState('detail');
  const [month,   setMonth]   = useState(curMonth());
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [data,    setData]    = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchSalary(); }, [month]);
  useEffect(() => { fetchHistory(); }, []);

  const fetchSalary = async () => {
    try { setLoading(true); const { data:res } = await api.get(`/teacher/salary/${month}`); setData(res); }
    catch { toast.error('Failed to load salary'); } finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try { const { data:res } = await api.get('/teacher/salary/history'); setHistory(Array.isArray(res)?res:[]); }
    catch {}
  };

  /* ── Derived ── */
  const days        = data?.days        || [];
  const savedRecord = data?.savedRecord || null;

  const totals = data ? {
    daysPresent:       data.daysPresent       || 0,
    daysAbsent:        data.daysAbsent        || 0,
    daysHoliday:       data.daysHoliday       || 0,
    daysLeavePaid:     data.daysLeavePaid     || 0,
    daysLeaveHalf:     data.daysLeaveHalf     || 0,
    daysLeaveUnpaid:   data.daysLeaveUnpaid   || 0,
    totalLateMinutes:  data.totalLateMinutes  || 0,
    monthlySalary:     data.monthlySalary     || 0,
    workingDays:       data.workingDays       || 30,
    absentDeduction:   data.absentDeduction   || 0,
    lateDeduct:        data.lateDeduct        || 0,
    unpaidLeaveDeduct: data.unpaidLeaveDeduct || 0,
    halfLeaveDeduct:   data.halfLeaveDeduct   || 0,
    vacationDays:      data.vacationDays      || 0,
    vacationDeduct:    data.vacationDeduct    || 0,
    totalDeductions:   data.totalDeductions   || 0,
    netSalary:         data.netSalary         || 0,
    overtimeRate:      data.overtimeRate      || 0,
    overtimeHours:     savedRecord?.overtimeHours  || 0,
    overtimeAmount:    savedRecord?.overtimeAmount || 0,
    incentive:         savedRecord?.incentive      || 0,
    incentiveNote:     savedRecord?.incentiveNote  || '',
    bonuses:           savedRecord?.bonuses        || 0,
    allowances:        savedRecord?.allowances     || 0,
  } : null;

  const finalNet = totals
    ? totals.netSalary + totals.overtimeAmount + totals.incentive + totals.bonuses + totals.allowances
    : 0;

  const dayTotals = days.reduce((acc, d) => ({
    regularPay:  acc.regularPay  + (d.regularPay  || 0),
    overtimePay: acc.overtimePay + (d.overtimePay || 0),
    incentive:   acc.incentive   + (d.incentive   || 0),
    deductions:  acc.deductions  + (d.deductions  || 0),
    netDay:      acc.netDay      + (d.netDay       || 0),
  }), { regularPay:0, overtimePay:0, incentive:0, deductions:0, netDay:0 });

  /* ── Export ── */
  const handleExport = (fmt_) => {
    if (view === 'detail' && days.length) {
      const rows = days.map(d => ({
        Date:            fmtDate(d.date),
        Day:             dayOfWeek(d.date),
        Status:          d.status,
        'Leave Type':    d.leaveType || '',
        'Check In':      d.checkIn  ? new Date(d.checkIn).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '',
        'Check Out':     d.checkOut ? new Date(d.checkOut).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '',
        'Late (min)':    d.lateMinutes || 0,
        'Regular Pay':   d.regularPay  || 0,
        'OT Pay':        d.overtimePay || 0,
        'Incentive':     d.incentive   || 0,
        'Deductions':    d.deductions  || 0,
        'Net':           d.netDay      || 0,
      }));
      // Append summary as extra rows
      if (totals) {
        rows.push({});  // blank separator
        rows.push({ Date:'SUMMARY', Day:'', Status:'', 'Leave Type':'', 'Check In':'', 'Check Out':'', 'Late (min)':'' });
        rows.push({ Date:'Monthly Salary',   Day: `Rs.${fmt(totals.monthlySalary)}` });
        if (totals.absentDeduction>0)   rows.push({ Date:'Absent Deduction',   Day:`-Rs.${fmt(totals.absentDeduction)}` });
        if (totals.lateDeduct>0)        rows.push({ Date:'Late Penalty',        Day:`-Rs.${fmt(totals.lateDeduct)}` });
        if (totals.unpaidLeaveDeduct>0) rows.push({ Date:'Unpaid Leave',        Day:`-Rs.${fmt(totals.unpaidLeaveDeduct)}` });
        if (totals.overtimeAmount>0)    rows.push({ Date:'Overtime',            Day:`+Rs.${fmt(totals.overtimeAmount)}` });
        if (totals.incentive>0)         rows.push({ Date:'Incentive',           Day:`+Rs.${fmt(totals.incentive)}` });
        if (totals.bonuses>0)           rows.push({ Date:'Bonuses',             Day:`+Rs.${fmt(totals.bonuses)}` });
        if (totals.allowances>0)        rows.push({ Date:'Allowances',          Day:`+Rs.${fmt(totals.allowances)}` });
        rows.push({ Date:'Net Salary', Day:`Rs.${fmt(finalNet)}` });
      }
      fmt_==='csv' ? exportCSV(rows,`Salary_${month}`) : exportXLSX(rows,`Salary_${month}`);
    } else if (view === 'yearly' && history.length) {
      const rows = history.map(h => ({
        Month:       h.month,
        'Net Salary':h.netSalary || 0,
        Status:      h.status    || '',
        Present:     h.daysPresent || 0,
        Absent:      h.daysAbsent  || 0,
        Incentive:   h.incentive   || 0,
        Bonuses:     h.bonuses     || 0,
      }));
      fmt_==='csv' ? exportCSV(rows,`Salary_${year}`) : exportXLSX(rows,`Salary_${year}`);
    } else { toast.error('No data to export'); }
  };

  /* ── Render ── */
  return (
    <div className="tsal"><style>{style}</style>
      <div className="tsal-title">My Salary</div>
      <div className="tsal-sub">View your monthly salary breakdown and history</div>

      {/* Tabs */}
      <div className="tsal-tabs">
        <button className={`tsal-tab ${view==='detail' ?'active':''}`} onClick={()=>setView('detail')}>Monthly Detail</button>
        <button className={`tsal-tab ${view==='yearly' ?'active':''}`} onClick={()=>setView('yearly')}>Yearly Overview</button>
        <button className={`tsal-tab ${view==='history'?'active':''}`} onClick={()=>setView('history')}>Salary History</button>
      </div>

      {/* ══ MONTHLY DETAIL ══ */}
      {view==='detail' && (
        <>
          <div className="tsal-toolbar">
            <input type="month" className="tsal-month" value={month} onChange={e=>setMonth(e.target.value)} />
            <div className="export-btns">
              <button className="btn-exp csv"  onClick={()=>handleExport('csv')}>⬇ CSV</button>
              <button className="btn-exp xlsx" onClick={()=>handleExport('xlsx')}>⬇ XLSX</button>
            </div>
          </div>

          {loading ? (
            <div className="tsal-loading"><div className="tsal-spin"/>Loading…</div>
          ) : !totals ? (
            <div className="tsal-panel"><div className="no-data">No salary data for {monthLabel(month)}.</div></div>
          ) : (
            <>
              {/* Stat cards — now includes Incentives */}
              <div className="stat-grid">
                <div className="stat-card">
                  <div className="s-icon green"><CalendarDaysIcon/></div>
                  <div><div className="s-label">Present</div><div className="s-value">{totals.daysPresent}</div><div className="s-sub">{totals.daysAbsent} absent · {totals.daysHoliday} holiday</div></div>
                </div>
                <div className="stat-card">
                  <div className="s-icon blue"><CheckCircleIcon/></div>
                  <div><div className="s-label">On Leave</div><div className="s-value">{totals.daysLeavePaid+totals.daysLeaveHalf+totals.daysLeaveUnpaid}</div><div className="s-sub">{totals.daysLeavePaid}P · {totals.daysLeaveHalf}H · {totals.daysLeaveUnpaid}U</div></div>
                </div>
                <div className="stat-card">
                  <div className="s-icon amber"><ClockIcon/></div>
                  <div><div className="s-label">Late</div><div className="s-value">{totals.totalLateMinutes} min</div><div className="s-sub">Rs. {fmt(totals.lateDeduct)} deducted</div></div>
                </div>
                <div className="stat-card">
                  <div className="s-icon red"><ExclamationTriangleIcon/></div>
                  <div><div className="s-label">Deductions</div><div className="s-value">{fmt(totals.totalDeductions)}</div><div className="s-sub">absent+late+leave{totals.vacationDays>0?'+vacation':''}</div></div>
                </div>
                {/* Incentives card — only if non-zero, otherwise shows 0 gracefully */}
                <div className="stat-card">
                  <div className="s-icon purple"><GiftIcon/></div>
                  <div>
                    <div className="s-label">Incentives</div>
                    <div className="s-value" style={{color: totals.incentive>0?'#8b5cf6':'#0f1123'}}>{fmt(totals.incentive+totals.bonuses+totals.allowances)}</div>
                    <div className="s-sub">{totals.incentive>0?`+Rs.${fmt(totals.incentive)} bonus`:'no bonus yet'}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="s-icon green"><BanknotesIcon/></div>
                  <div><div className="s-label">Net Salary</div><div className="s-value" style={{color:'#10c98e'}}>{fmt(finalNet)}</div><div className="s-sub">of {fmt(totals.monthlySalary)}/mo</div></div>
                </div>
              </div>

              {/* Day table */}
              <div className="tsal-panel">
                <div className="tsal-panel-hd">Day-by-Day — {monthLabel(month)}</div>
                {!days.length ? (
                  <div className="no-data">No attendance records this month.</div>
                ) : (
                  <>
                    <div className="tsal-tbl-wrap">
                      <table className="tsal-tbl">
                        <thead>
                          <tr>
                            <th>Date</th><th>Day</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Late (min)</th>
                            <th>Regular Pay</th><th>OT Pay</th><th style={{color:'#8b5cf6'}}>Incentive</th><th>Deductions</th><th>Net</th><th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {days.map((d,i) => (
                            <tr key={i}>
                              <td><span className="mono">{fmtDate(d.date)}</span></td>
                              <td style={{fontSize:'.74rem',color:'#9094a8'}}>{dayOfWeek(d.date)}</td>
                              <td>
                                {d.status==='holiday'?<span className="status-pill holiday">🎉 Holiday</span>
                                :d.status==='off'    ?<span className="status-pill off">📅 Off Day</span>
                                :<span className={`status-pill ${d.status}`}>{d.status}{d.status==='on-leave'&&d.leaveType?` (${d.leaveType})`:''}</span>}
                              </td>
                              <td><span className="mono" style={{color:'#6b7194'}}>{d.checkIn?new Date(d.checkIn).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}):'—'}</span></td>
                              <td><span className="mono" style={{color:'#6b7194'}}>{d.checkOut?new Date(d.checkOut).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}):'—'}</span></td>
                              <td><span className="mono" style={{color:(d.lateMinutes||0)>0?'#f59e0b':'#b0b5cc'}}>{d.lateMinutes>0?d.lateMinutes:'—'}</span></td>
                              <td><span className="mono">{fmt(d.regularPay)}</span></td>
                              <td>{(d.overtimePay||0)>0?<span className="mono" style={{color:'#8b5cf6'}}>{fmt(d.overtimePay)}</span>:<span className="mono" style={{color:'#c0c5dc'}}>—</span>}</td>
                              <td>{(d.incentive||0)>0?<span className="mono" style={{color:'#8b5cf6'}}>{fmt(d.incentive)}</span>:<span className="mono" style={{color:'#c0c5dc'}}>—</span>}</td>
                              <td>{(d.deductions||0)>0?<span className="mono" style={{color:'#ef4444'}}>−{fmt(d.deductions)}</span>:<span className="mono" style={{color:'#c0c5dc'}}>—</span>}</td>
                              <td><span className="mono" style={{color:'#10c98e',fontWeight:600}}>{fmt(d.netDay)}</span></td>
                              <td style={{fontSize:'.72rem',color:'#9094a8'}}>{d.status==='on-leave'&&d.leaveType?`${d.leaveType} leave`:d.status==='holiday'?'Paid holiday':''}</td>
                            </tr>
                          ))}
                          <tr className="total-row">
                            <td colSpan={2}>Monthly Total</td><td></td><td></td><td></td>
                            <td><span className="mono" style={{color:'#f59e0b'}}>{totals.totalLateMinutes} min</span></td>
                            <td><span className="mono">{fmt(dayTotals.regularPay)}</span></td>
                            <td>{dayTotals.overtimePay>0?<span className="mono" style={{color:'#8b5cf6'}}>{fmt(dayTotals.overtimePay)}</span>:<span className="mono" style={{color:'#c0c5dc'}}>—</span>}</td>
                            <td>{dayTotals.incentive>0?<span className="mono" style={{color:'#8b5cf6'}}>{fmt(dayTotals.incentive)}</span>:<span className="mono" style={{color:'#c0c5dc'}}>—</span>}</td>
                            <td>{dayTotals.deductions>0?<span className="mono" style={{color:'#ef4444'}}>−{fmt(dayTotals.deductions)}</span>:<span className="mono" style={{color:'#c0c5dc'}}>—</span>}</td>
                            <td><span className="mono" style={{color:'#10c98e'}}>{fmt(dayTotals.netDay)}</span></td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile cards */}
                    <div className="tsal-cards">
                      {days.map((d,i) => {
                        const note = d.status==='on-leave'&&d.leaveType?`${d.leaveType} leave`:d.status==='holiday'?'Paid holiday':'';
                        return (
                          <div key={i} className="tsal-card">
                            <div className="tc-top">
                              <div>
                                <div className="tc-date">{fmtDate(d.date)}</div>
                                <div className="tc-day">{dayOfWeek(d.date)}</div>
                              </div>
                              {d.status==='holiday'?<span className="status-pill holiday">🎉 Holiday</span>
                              :d.status==='off'    ?<span className="status-pill off">📅 Off Day</span>
                              :<span className={`status-pill ${d.status}`}>{d.status}{d.status==='on-leave'&&d.leaveType?` (${d.leaveType})`:''}</span>}
                            </div>
                            <div className="tc-row"><span className="tc-label">Check In</span><span className="mono" style={{color:'#6b7194'}}>{d.checkIn?new Date(d.checkIn).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}):'—'}</span></div>
                            <div className="tc-row"><span className="tc-label">Check Out</span><span className="mono" style={{color:'#6b7194'}}>{d.checkOut?new Date(d.checkOut).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}):'—'}</span></div>
                            {d.lateMinutes>0 && <div className="tc-row"><span className="tc-label">Late</span><span className="mono" style={{color:'#f59e0b'}}>{d.lateMinutes} min</span></div>}
                            <div className="tc-row"><span className="tc-label">Regular Pay</span><span className="mono">{fmt(d.regularPay)}</span></div>
                            {(d.overtimePay||0)>0 && <div className="tc-row"><span className="tc-label">OT Pay</span><span className="mono" style={{color:'#8b5cf6'}}>{fmt(d.overtimePay)}</span></div>}
                            {(d.incentive||0)>0 && <div className="tc-row"><span className="tc-label">Incentive</span><span className="mono" style={{color:'#8b5cf6'}}>{fmt(d.incentive)}</span></div>}
                            {(d.deductions||0)>0 && <div className="tc-row"><span className="tc-label">Deductions</span><span className="mono" style={{color:'#ef4444'}}>−{fmt(d.deductions)}</span></div>}
                            <div className="tc-row"><span className="tc-label">Net</span><span className="mono" style={{color:'#10c98e',fontWeight:700}}>{fmt(d.netDay)}</span></div>
                            {note && <div className="tc-row"><span className="tc-label">Note</span><span style={{fontSize:'.72rem',color:'#9094a8'}}>{note}</span></div>}
                          </div>
                        );
                      })}
                      <div className="tsal-card total">
                        <div className="tc-row"><span className="tc-label" style={{fontWeight:700,color:'#0f1123'}}>Monthly Total</span><span></span></div>
                        <div className="tc-row"><span className="tc-label">Late Minutes</span><span className="mono" style={{color:'#f59e0b',fontWeight:700}}>{totals.totalLateMinutes} min</span></div>
                        <div className="tc-row"><span className="tc-label">Regular Pay</span><span className="mono" style={{fontWeight:700}}>{fmt(dayTotals.regularPay)}</span></div>
                        {dayTotals.overtimePay>0 && <div className="tc-row"><span className="tc-label">OT Pay</span><span className="mono" style={{color:'#8b5cf6',fontWeight:700}}>{fmt(dayTotals.overtimePay)}</span></div>}
                        {dayTotals.incentive>0 && <div className="tc-row"><span className="tc-label">Incentives</span><span className="mono" style={{color:'#8b5cf6',fontWeight:700}}>{fmt(dayTotals.incentive)}</span></div>}
                        {dayTotals.deductions>0 && <div className="tc-row"><span className="tc-label">Deductions</span><span className="mono" style={{color:'#ef4444',fontWeight:700}}>−{fmt(dayTotals.deductions)}</span></div>}
                        <div className="tc-row"><span className="tc-label">Net</span><span className="mono" style={{color:'#10c98e',fontWeight:700}}>{fmt(dayTotals.netDay)}</span></div>
                      </div>
                    </div>
                  </>
                )}

                {/* Salary Breakdown */}
                <div className="breakdown">
                  <div className="breakdown-title">Salary Breakdown — {monthLabel(month)}</div>
                  <div className="brow"><span style={{color:'#6b7194'}}>Monthly Salary</span><span style={{fontWeight:700}}>Rs. {fmt(totals.monthlySalary)}</span></div>
                  {totals.absentDeduction>0   &&<div className="brow"><span style={{color:'#ef4444'}}>− Absent ({totals.daysAbsent} days)</span><span style={{color:'#ef4444',fontWeight:600}}>Rs. {fmt(totals.absentDeduction)}</span></div>}
                  {totals.unpaidLeaveDeduct>0  &&<div className="brow"><span style={{color:'#ef4444'}}>− Unpaid Leave ({totals.daysLeaveUnpaid} days)</span><span style={{color:'#ef4444',fontWeight:600}}>Rs. {fmt(totals.unpaidLeaveDeduct)}</span></div>}
                  {totals.halfLeaveDeduct>0    &&<div className="brow"><span style={{color:'#f59e0b'}}>− Half-paid Leave ({totals.daysLeaveHalf} days)</span><span style={{color:'#f59e0b',fontWeight:600}}>Rs. {fmt(totals.halfLeaveDeduct)}</span></div>}
                  {totals.lateDeduct>0         &&<div className="brow"><span style={{color:'#f59e0b'}}>− Late Penalty ({totals.totalLateMinutes} min)</span><span style={{color:'#f59e0b',fontWeight:600}}>Rs. {fmt(totals.lateDeduct)}</span></div>}
                  {totals.vacationDays>0       &&<div className="brow"><span style={{color:'#8b5cf6'}}>− Vacation ({totals.vacationDays} days)</span><span style={{color:'#8b5cf6',fontWeight:600}}>Rs. {fmt(totals.vacationDeduct)}</span></div>}
                  {totals.overtimeAmount>0     &&<div className="brow"><span style={{color:'#10c98e'}}>+ Overtime ({totals.overtimeHours} hrs × Rs.{fmt(totals.overtimeRate)}/hr)</span><span style={{color:'#10c98e',fontWeight:600}}>Rs. {fmt(totals.overtimeAmount)}</span></div>}
                  {totals.incentive>0          &&<div className="brow"><span style={{color:'#8b5cf6'}}>+ Incentive{totals.incentiveNote?` (${totals.incentiveNote})`:''}</span><span style={{color:'#8b5cf6',fontWeight:600}}>Rs. {fmt(totals.incentive)}</span></div>}
                  {totals.bonuses>0            &&<div className="brow"><span style={{color:'#10c98e'}}>+ Bonuses</span><span style={{color:'#10c98e',fontWeight:600}}>Rs. {fmt(totals.bonuses)}</span></div>}
                  {totals.allowances>0         &&<div className="brow"><span style={{color:'#10c98e'}}>+ Allowances</span><span style={{color:'#10c98e',fontWeight:600}}>Rs. {fmt(totals.allowances)}</span></div>}
                  <div className="brow-net"><span>Net Salary</span><span style={{color:'#10c98e'}}>Rs. {fmt(finalNet)}</span></div>
                  {!savedRecord && <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:6}}>ℹ️ Salary not yet finalized by admin</div>}
                  {savedRecord  && <div style={{fontSize:'.72rem',color:'#10c98e',marginTop:6}}>✓ Finalized{savedRecord.finalizedBy?` by ${savedRecord.finalizedBy}`:''}{savedRecord.finalizedAt?` on ${new Date(savedRecord.finalizedAt).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'})}`:''}</div>}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ══ YEARLY OVERVIEW ══ */}
      {view==='yearly' && (
        <>
          <div className="tsal-toolbar">
            <input type="number" className="tsal-year" value={year} min="2020" max="2099" onChange={e=>setYear(Number(e.target.value))} />
            <div className="export-btns">
              <button className="btn-exp csv"  onClick={()=>handleExport('csv')}>⬇ CSV</button>
              <button className="btn-exp xlsx" onClick={()=>handleExport('xlsx')}>⬇ XLSX</button>
            </div>
          </div>
          <div className="yr-grid">
            {Array.from({length:12},(_,i)=>{
              const m    = `${year}-${String(i+1).padStart(2,'0')}`;
              const saved= history.find(h=>h.month===m);
              const pct  = saved&&saved.daysPresent!=null&&saved.workingDays>0 ? Math.round((saved.daysPresent/saved.workingDays)*100) : null;
              return (
                <div key={m} className="yr-card" onClick={()=>{setMonth(m);setView('detail');}}>
                  <div className="yr-card-month">{MONTHS[i]} {year}</div>
                  {saved ? (
                    <>
                      <div className="yr-card-net" style={{color:'#10c98e'}}>Rs. {fmt(saved.netSalary)}</div>
                      <div className="yr-card-info">{saved.daysPresent||0}P · {saved.daysAbsent||0}A{pct!==null?` · ${pct}%`:''}</div>
                      {(saved.incentive||0)>0 && <div style={{fontSize:'.7rem',color:'#8b5cf6',fontWeight:600,marginBottom:4}}>🎁 +Rs.{fmt(saved.incentive)} incentive</div>}
                      <span style={{fontSize:'.68rem',fontWeight:700,padding:'2px 8px',borderRadius:99,background:saved.status==='paid'?'#ecfdf5':'#fffbeb',color:saved.status==='paid'?'#10c98e':'#f59e0b'}}>
                        {saved.status==='paid'?'✓ Paid':'Pending'}
                      </span>
                    </>
                  ) : (
                    <div style={{fontSize:'.74rem',color:'#c0c5dc',marginTop:4}}>Not finalized</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ══ SALARY HISTORY ══ */}
      {view==='history' && (
        history.length===0 ? (
          <div className="tsal-panel"><div className="no-data">No finalized salary records yet.</div></div>
        ) : (
          <div className="hist-grid">
            {history.map(h => (
              <div key={h._id} className="hist-card" onClick={()=>{setMonth(h.month);setView('detail');}}>
                <div className="hist-month">{monthLabel(h.month)}</div>
                <div className="hist-net">Rs. {fmt(h.netSalary)}</div>
                <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:3}}>{h.daysPresent||0}P · {h.daysAbsent||0}A</div>
                {(h.incentive||0)>0 && <div style={{fontSize:'.7rem',color:'#8b5cf6',fontWeight:600,marginTop:2}}>🎁 +Rs.{fmt(h.incentive)}</div>}
                <div className="hist-status">
                  <span style={{fontSize:'.68rem',fontWeight:700,padding:'2px 8px',borderRadius:99,background:h.status==='paid'?'#ecfdf5':'#fffbeb',color:h.status==='paid'?'#10c98e':'#f59e0b'}}>
                    {h.status==='paid'?'✓ Paid':'Unpaid'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default TeacherSalary;